<!--
SPDX-FileCopyrightText: 2025 Land Baden-Württemberg <InnoLab@stm.bwl.de>

SPDX-License-Identifier: MPL-2.0
-->

<script>
import { afterUpdate, onMount } from 'svelte';
import documentStore from '../utils/documentStore';
import websocketsStore from '../utils/websocketsStore';

export let newMessage = '';
export let name = '';
export let placeholder = '';
export let handleEnterKey = () => console.log('No callback is given'); //pass any key event zu parent
export const reset = () => resetTextArea(); //call reset textare from parent
export let isTextFieldActive = false;
export let resizable = false;
export let showFileUpload = true;
export let uploadLocked = false;
export let maxChars = null; // optional, z.B. 4000 für Chat-Eingabe
export let showOnlineSearch = false;
export let onlineSearchEnabled = false;
/** 'none' | 'websearch' (blue) | 'remote' (red, cloud model) */
export let inputPrivacyTone = 'none';

// Allowed file types
const ALLOWED_EXTENSIONS = ['.pdf', '.docx', '.txt'];
const ALLOWED_MIME_TYPES = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'text/plain',
];

let isLimitReached = false;
let textarea;
let docStats = {
  count: 0,
  maxCount: documentStore.MAX_DOCUMENTS,
  isDocLimitReached: false,
  remainingDocSlots: documentStore.MAX_DOCUMENTS,
  totalChars: 0,
  activeChars: 0,
  remainingChars: documentStore.MAX_TOTAL_CHARS,
  charLimit: documentStore.MAX_TOTAL_CHARS,
  tokenLimit: documentStore.MAX_TOTAL_TOKENS,
  remainingTokens: documentStore.MAX_TOTAL_TOKENS,
  charUsagePercent: 0,
  isCharLimitExceeded: false,
};
let usagePercent = 0;
let prevLimitReached = false;
let hasInitializedDocLimit = false;
let hasShownCharLimitWarning = false;
let isActionsMenuOpen = false;
let actionsMenuRef;
let fileInputRef;

function autoResize() {
  if (!textarea) {
    return;
  }
  textarea.style.height = 'auto';
  const newHeight = Math.min(textarea.scrollHeight, 200);
  textarea.style.height = `${newHeight}px`;
}

documentStore.documentStats.subscribe((stats) => {
  docStats = stats;
  usagePercent = Math.min(100, Math.round(stats.charUsagePercent || 0));

  const newLimitReached = stats.isDocLimitReached;
  isLimitReached = newLimitReached;

  // On first subscription, just initialize state without showing a toast.
  // This prevents a warning when the component mounts while the limit is
  // already reached from a previous session / route.
  if (!hasInitializedDocLimit) {
    prevLimitReached = newLimitReached;
    hasInitializedDocLimit = true;
    return;
  }

  // Trigger toast when upload limit is reached
  if (newLimitReached && !prevLimitReached && stats.count > 0) {
    websocketsStore.currentMessage.warning.set({
      message: `Maximum von ${documentStore.MAX_DOCUMENTS} Dokumenten erreicht. Bitte löschen Sie Dokumente, um weitere hinzuzufügen.`,
      type: 'warning',
    });
    documentStore.hasShownLimitWarning.set(true);
  }

  prevLimitReached = newLimitReached;
});

// Zeichenlimit für chat erzwingen
$: if (maxChars && typeof newMessage === 'string') {
  const limit = Number(maxChars);
  if (Number.isFinite(limit) && limit > 0) {
    if (newMessage.length > limit) {
      newMessage = newMessage.slice(0, limit);
      if (!hasShownCharLimitWarning) {
        websocketsStore.currentMessage.warning.set({
          message: `Die maximale Eingabelänge von ${limit} Zeichen wurde erreicht. Bitte kürzen Sie Ihren Text.`,
          type: 'warning',
        });
        hasShownCharLimitWarning = true;
      }
    } else if (newMessage.length < limit) {
      // Zurücksetzen, damit bei einem erneuten Überschreiten erneut gewarnt wird
      hasShownCharLimitWarning = false;
    }
  }
}

$: uploadTooltip = uploadLocked
  ? 'Während die Antwort generiert wird, können keine Dokumente verwaltet oder hochgeladen werden.'
  : isLimitReached
    ? `Maximum von ${documentStore.MAX_DOCUMENTS} Dokumenten erreicht`
    : `PDF, DOCX oder TXT hochladen (${docStats.count}/${documentStore.MAX_DOCUMENTS} Dokumente, ${usagePercent}% des Textlimits belegt)`;
$: onlineSearchTooltip = onlineSearchEnabled ? 'Online-Suche ist aktiviert' : 'Online-Suche ist deaktiviert';
$: if (!showOnlineSearch && onlineSearchEnabled) {
  onlineSearchEnabled = false;
}

function toggleActionsMenu() {
  isActionsMenuOpen = !isActionsMenuOpen;
}

function toggleWebsearch() {
  onlineSearchEnabled = !onlineSearchEnabled;
}

function openFilePicker() {
  if (isLimitReached || uploadLocked) {
    return;
  }
  if (fileInputRef) {
    fileInputRef.click();
  }
}

onMount(() => {
  if (!textarea) {
    return;
  }

  const handleInput = () => autoResize();

  const handleKeydown = (event) => {
    if (event.key === 'Enter' && event.shiftKey) {
      event.preventDefault();

      const cursorPosition = textarea.selectionStart;
      const currentValue = textarea.value;

      const newValue = `${currentValue.slice(0, cursorPosition)}\n${currentValue.slice(cursorPosition)}`;

      textarea.value = newValue;
      textarea.selectionStart = textarea.selectionEnd = cursorPosition + 1;

      autoResize();
    }
  };

  textarea.addEventListener('input', handleInput);
  textarea.addEventListener('keydown', handleKeydown);

  if (resizable) {
    textarea.style.height = 'auto';
    const textHeight = textarea.scrollHeight;
    textarea.style.height = textHeight < 400 ? `${textHeight}px` : '400px';
  } else {
    autoResize();
  }

  return () => {
    if (!textarea) {
      return;
    }
    textarea.removeEventListener('input', handleInput);
    textarea.removeEventListener('keydown', handleKeydown);
  };
});

afterUpdate(() => {
  if (textarea) {
    autoResize();
  }
});

onMount(() => {
  const handleDocumentClick = (event) => {
    if (!actionsMenuRef || actionsMenuRef.contains(event.target)) {
      return;
    }
    isActionsMenuOpen = false;
  };

  document.addEventListener('click', handleDocumentClick);
  return () => {
    document.removeEventListener('click', handleDocumentClick);
  };
});

function resetTextArea() {
  if (textarea != null) {
    textarea.style.height = '60px';
  }
}

function isValidFileType(file) {
  const fileName = file.name.toLowerCase();
  const fileType = file.type.toLowerCase();

  // Check by extension
  const hasValidExtension = ALLOWED_EXTENSIONS.some((ext) => fileName.endsWith(ext.toLowerCase()));

  // Check by MIME type
  const hasValidMimeType = ALLOWED_MIME_TYPES.some((mime) => fileType.includes(mime));

  return hasValidExtension || hasValidMimeType;
}

async function handleFileUpload(event) {
  const files = event.target.files;
  if (!files) return;
  if (uploadLocked) {
    event.target.value = '';
    return;
  }

  const fileArray = Array.from(files);
  const validFiles = [];
  const rejectedType = [];

  fileArray.forEach((file) => {
    if (!isValidFileType(file)) {
      rejectedType.push(file);
    } else {
      validFiles.push(file);
    }
  });

  if (rejectedType.length > 0) {
    const fileList = rejectedType.map((f) => f.name).join(', ');
    websocketsStore.currentMessage.error.set({
      message: `Nur PDF, DOCX und TXT erlaubt: ${fileList}`,
      type: 'error',
    });
  }

  if (validFiles.length > 0) {
    // Create a new FileList-like object for addFiles
    const dt = new DataTransfer();
    validFiles.forEach((file) => dt.items.add(file));
    try {
      const result = await documentStore.addFiles(dt.files);

      if (result) {
        const tooLargeNames = Array.isArray(result.tooLarge) ? result.tooLarge : [];
        const failedNames = Array.isArray(result.failed) ? result.failed : [];

        if (Array.isArray(result.duplicates) && result.duplicates.length > 0) {
          const duplicateList = result.duplicates.map((name) => `<strong>${name}</strong>`).join(', ');
          const duplicateMessage =
            result.duplicates.length === 1
              ? `Das Dokument ${duplicateList} wurde nicht erneut angehängt, da das gleiche Dokument bereits vorhanden ist.`
              : `Diese Dokumente wurden nicht erneut angehängt, da gleiche Dokumente bereits vorhanden ist: ${duplicateList}`;
          websocketsStore.currentMessage.info.set({
            message: duplicateMessage,
            type: 'info',
            persistent: true,
          });
        }

        if (Array.isArray(result.deactivatedDueToLimit) && result.deactivatedDueToLimit.length > 0) {
          const list = result.deactivatedDueToLimit.join(', ');
          const warningMessage =
            result.deactivatedDueToLimit.length === 1
              ? `Das Dokument ${list} wurde deaktiviert, da das Textlimit bereits erreicht ist. Bitte deaktivieren Sie andere Dokumente oder reduzieren Sie den Umfang.`
              : `Folgende Dokumente wurden deaktiviert, da das Textlimit bereits erreicht ist: ${list}. Bitte deaktivieren Sie andere Dokumente oder reduzieren Sie den Umfang.`;
          websocketsStore.currentMessage.warning.set({
            message: warningMessage,
            type: 'warning',
            persistent: true,
          });
        }

        if (tooLargeNames.length > 0 || failedNames.length > 0) {
          const errorParts = [];

          if (tooLargeNames.length > 0) {
            const tooLargeList = tooLargeNames.join(', ');
            const tooLargeMessage =
              tooLargeNames.length === 1
                ? `Dokument ${tooLargeList} kann nicht genutzt werden, weil es zu groß ist.`
                : `Folgende Dokumente können nicht genutzt werden, weil sie zu groß sind: ${tooLargeList}.`;
            errorParts.push(tooLargeMessage);
          }

          if (failedNames.length > 0) {
            const failedList = failedNames.join(', ');
            const failedMessage =
              failedNames.length === 1
                ? `Dokument ${failedList} konnte nicht verarbeitet werden.`
                : `Diese Dokumente konnten nicht verarbeitet werden: ${failedList}.`;
            errorParts.push(failedMessage);
          }

          websocketsStore.currentMessage.error.set({
            message: errorParts.join(' '),
            type: 'error',
            persistent: true,
          });
        }
      }
    } catch (error) {
      console.error('Fehler beim Hinzufügen der Dokumente:', error);
      websocketsStore.currentMessage.error.set({
        message: 'Die Dokumente konnten nicht angehängt werden. Bitte versuchen Sie es erneut.',
        type: 'error',
      });
    }
  }

  // Reset input
  event.target.value = '';
  isActionsMenuOpen = false;
}
</script>

<div
        class="chat-eingabe"
        class:websearch-active={inputPrivacyTone === 'websearch'}
        class:remote-model-active={inputPrivacyTone === 'remote'}
>
    {#if showFileUpload}
    <div class="file-upload-wrapper">
        <div class="actions-menu-container" bind:this={actionsMenuRef}>
            <button
                type="button"
                class="btn secondary plus-button"
                aria-expanded={isActionsMenuOpen}
                aria-label="Aktionen öffnen"
                on:click={toggleActionsMenu}
            >
                +
            </button>

            {#if isActionsMenuOpen}
                <div
                    class="actions-dropdown"
                    on:keydown={(event) => {
                      if (event.key === 'Escape') {
                        isActionsMenuOpen = false;
                      }
                    }}
                >
                    <button
                        type="button"
                        class="menu-item"
                        class:disabled={isLimitReached || uploadLocked}
                        aria-disabled={isLimitReached || uploadLocked}
                        title={uploadTooltip}
                        on:click={openFilePicker}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg"
                             fill="none"
                             viewBox="0 0 24 24"
                             stroke-width="2"
                             stroke="currentColor"
                             class="menu-icon">
                            <path stroke-linecap="round"
                                  stroke-linejoin="round"
                                  d="M21.44 11.05l-9.19 9.19a5 5 0 11-7.07-7.07l9.19-9.19a3 3 0 014.24 4.24l-9.19 9.19a1 1 0 11-1.41-1.41l9.19-9.19"/>
                        </svg>
                        <span>Dateien anhängen</span>
                        <input id="file-upload" type="file" multiple class="hidden-input"
                               bind:this={fileInputRef}
                               accept=".pdf,.docx,.txt,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain"
                               disabled={isLimitReached || uploadLocked}
                               title={uploadTooltip}
                               on:change={handleFileUpload}/>
                    </button>

                    {#if showOnlineSearch}
                        <button
                            type="button"
                            class="menu-item"
                            aria-pressed={onlineSearchEnabled}
                            title={onlineSearchTooltip}
                            on:click={toggleWebsearch}
                        >
                            <img
                                class="menu-icon websearch-toggle-icon"
                                src={onlineSearchEnabled ? 'static/websearch-on.svg' : 'static/websearch-off.svg'}
                                alt=""
                                aria-hidden="true"
                            />
                            <span>{onlineSearchEnabled ? 'Websuche deaktivieren' : 'Websuche aktivieren'}</span>
                        </button>
                    {/if}
                </div>
            {/if}
        </div>

        <div class="document-counter-wrapper">
            <span
                class="document-counter"
                class:warning={!isLimitReached && docStats.count >= docStats.maxCount - 5}
                class:danger={isLimitReached}
                title={`Maximal ${documentStore.MAX_DOCUMENTS} Dokumente`}
            >
                {docStats.count}/{docStats.maxCount}
                <span class="document-label">Dokumente</span>
            </span>
        </div>
    </div>
    {/if}

<textarea
        bind:this={textarea}
        style="resize: {resizable ? 'true' : 'none'}; max-height: {resizable
  ? 'auto'
  : '400px'};"
        id="auto-resize-textarea"
        on:keypress={(event) => {
            if (uploadLocked) {
                if (event.key === "Enter" && !event.shiftKey) {
                    event.preventDefault();
                    websocketsStore.currentMessage.info.set({
                        message:
                            "Bitte warten Sie, bis die aktuelle Antwort abgeschlossen ist, bevor Sie eine neue Anfrage senden.",
                        type: "info",
                    });
                }
                return;
            }
            handleEnterKey(event);
        }}
        bind:value={newMessage}
        {name}
        {placeholder}
        on:blur={() => (isTextFieldActive = false)}
        on:focus={() => (isTextFieldActive = true)}
></textarea>

    {#if $$slots['inside-input']}
            <slot name="inside-input" />
    {/if}
</div>

<style>
    .chat-eingabe {
        display: flex;
        align-items: center;
        position: relative;
        border: 1px solid #d0d0d0;
        border-radius: 12px;
        padding: 10px;
        background-color: #fff;
        transition: border-color 0.2s, border-width 0.2s;
        min-height: 68px;
    }

    .chat-eingabe.websearch-active {
        --privacy-border: #7898bd;
        border-width: 2px;
        border-color: var(--privacy-border);
    }

    .chat-eingabe.remote-model-active {
        --privacy-border: #920303;
        border-width: 2px;
        border-color: var(--privacy-border);
    }

    .chat-eingabe:focus-within {
        border-color: #d0d0d0;
    }

    .chat-eingabe.websearch-active:focus-within {
        border-color: #6a89ad;
    }

    .chat-eingabe.remote-model-active:focus-within {
        border-color: #6d0202;
    }

    .chat-eingabe textarea {
        flex: 1;
        min-width: 0;
    }

    .chat-eingabe textarea:focus {
        outline: none;
    }

    .file-upload-wrapper {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        margin-right: 12px;
        align-self: center;
        justify-content: center;
    }

    .actions-menu-container {
        position: relative;
        display: flex;
        align-items: center;
        justify-content: center;
    }

    .plus-button {
        min-width: 40px;
        width: 40px;
        height: 40px;
        padding: 0;
        border: 1px solid #e0e0e0 !important;
        border-radius: 50% !important;
        background-color: #f9f9f9;
        color: #555;
        font-size: 1.8rem;
        font-weight: 400;
        line-height: 1;
    }

    .plus-button:hover {
        border-color: #999 !important;
        background-color: #f0f0f0;
        color: #222;
    }

    .actions-dropdown {
        position: absolute;
        left: 0;
        bottom: calc(100% + 8px);
        min-width: 260px;
        z-index: 20;
        background-color: #fff;
        border: 1px solid #e0e0e0;
        border-radius: 10px;
        box-shadow: 0 6px 16px rgba(0, 0, 0, 0.1);
        padding: 6px;
        display: flex;
        flex-direction: column;
        gap: 4px;
    }

    .menu-item {
        width: 100%;
        display: flex;
        align-items: center;
        justify-content: flex-start !important;
        gap: 8px;
        padding: 8px 10px;
        border-radius: 8px;
        border: none;
        background: transparent;
        color: #222;
        text-align: left;
        font-size: 0.95rem;
        font-weight: 500;
        text-transform: none !important;
        letter-spacing: normal;
        line-height: 1.2rem;
        cursor: pointer;
        margin: 0;
    }

    .menu-item:hover:not(.disabled) {
        background-color: #f4f4f4;
    }

    .menu-item.disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }

    .menu-icon {
        width: 18px;
        height: 18px;
        color: #555;
        flex-shrink: 0;
    }

    .websearch-toggle-icon {
        width: 20px;
        height: 20px;
    }

    .hidden-input {
        display: none;
    }

    .document-counter-wrapper {
        display: flex;
        justify-content: center;
        width: 100%;
        margin-top: 4px;
    }

    .document-counter {
        font-size: 0.75rem;
        color: #666;
        font-weight: 500;
        transition: color 0.2s ease;
    }

    .document-counter.warning {
        color: #f57c00;
    }

    .document-counter.danger {
        color: #c62828;
        font-weight: 600;
    }

    @media screen and (max-width: 640px) {

        .document-counter-wrapper {
            justify-content: flex-start;
        }

        .actions-dropdown {
            min-width: 220px;
        }

        .document-label {
            display: none;
        }
    }

    .document-label {
        margin-left: 4px;
    }
</style>
